import type { AppStore } from "@/store/AppStore";
import type {
  SearchCalendarEventsInput,
  SearchCalendarEventsOutput,
} from "@mem-client/ai-toolkit/contract";
import {
  AiToolkitToolName,
  searchCalendarEventsInputSchema,
  searchCalendarEventsOutputSchema,
} from "@mem-client/ai-toolkit/contract";
import type { AiToolkitToolDefinition } from "@/modules/ai-toolkit/tools/types";
import {
  DEFAULT_AI_TOOLKIT_SEARCH_LIMIT,
  matchesAiToolkitSearchQuery,
  normalizeAiToolkitSearchQuery,
} from "@/modules/ai-toolkit/tools/utils/local-search-helpers";
import { buildCalendarEvent } from "@/modules/ai-toolkit/tools/calendar/calendar-tools.helpers";
import { searchCalendarEventsByTimeRange } from "@/store/calendar/space-account-calendar-event/search-calendar-events";
import type { SpaceAccountCalendarEventObservable } from "@/store/calendar/space-account-calendar-event/SpaceAccountCalendarEventObservable";
import { DateTime } from "luxon";

const DEFAULT_SEARCH_WINDOW_HOURS_BEFORE = 6;
const DEFAULT_SEARCH_WINDOW_DAYS_AFTER = 14;

const isCalendarEvent = (
  event: SpaceAccountCalendarEventObservable | null | undefined
): event is SpaceAccountCalendarEventObservable => !!event;

const getDefaultSearchRange = (): { from: string; to: string } => {
  const now = DateTime.now();

  return {
    from: now.minus({ hours: DEFAULT_SEARCH_WINDOW_HOURS_BEFORE }).toISO() ?? "",
    to: now.plus({ days: DEFAULT_SEARCH_WINDOW_DAYS_AFTER }).toISO() ?? "",
  };
};

const matchesSearchQuery = ({
  event,
  query,
}: {
  event: SpaceAccountCalendarEventObservable;
  query: string | undefined;
}): boolean =>
  matchesAiToolkitSearchQuery({
    query,
    values: [
      event.primaryLabel,
      event.url,
      event.calendarName,
      event.calendarEmailAddress,
      event.associatedNote?.title,
    ],
  });

export const searchCalendarEvents = async ({
  store,
  input,
}: {
  store: AppStore;
  input: SearchCalendarEventsInput;
}): Promise<SearchCalendarEventsOutput> => {
  const defaultRange = getDefaultSearchRange();
  const from = input.from ?? defaultRange.from;
  const to = input.to ?? defaultRange.to;
  const query = normalizeAiToolkitSearchQuery(input.query);
  const eventIds = await searchCalendarEventsByTimeRange({
    store,
    startTime: from,
    endTime: to,
    includeHidden: input.includeHidden ?? false,
  });
  const events = (
    await Promise.all(eventIds.map(eventId => store.spaceAccountCalendarEvents.getAsync(eventId)))
  ).filter(isCalendarEvent);

  return {
    calendarEvents: events
      .filter(event => matchesSearchQuery({ event, query }))
      .slice(0, input.limit ?? DEFAULT_AI_TOOLKIT_SEARCH_LIMIT)
      .map(event => buildCalendarEvent({ event })),
  };
};

export const searchCalendarEventsTool: AiToolkitToolDefinition<
  AiToolkitToolName.SearchCalendarEvents,
  SearchCalendarEventsInput,
  SearchCalendarEventsOutput
> = {
  name: AiToolkitToolName.SearchCalendarEvents,
  inputSchema: searchCalendarEventsInputSchema,
  outputSchema: searchCalendarEventsOutputSchema,
  strict: true,
  execute: searchCalendarEvents,
};
